import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { ButtonFooter } from '~/components';
import { AddressForm } from '~/components/forms/GenericForms/AddressForm';
import { AddressFormValues, addressSchema } from '~/features/types';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { profileFormMachine } from '../profileForm.machine';

const TAGS = CT.profile.contactInfo.address.form;

interface WrapperProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
}

export const AddressFormWrapper = (props: WrapperProps) => {
  const { actor } = props;
  const addresses = useSelector(actor, (state) => state.context.addresses);
  const emailAddresses = useSelector(actor, (state) => state.context.emailAddresses);
  const phoneNumbers = useSelector(actor, (state) => state.context.phoneNumbers);
  const editAddressIndex = useSelector(actor, (state) => state.context.editAddressIndex);
  const create = useSelector(actor, (state) => state.context.modal.create);
  const addressForm = useSelector(actor, (state) => state.context.addressForm);

  const persist = (nextAddresses: AddressFormValues[]) => {
    actor.send({ type: 'ON_SUBMIT_ADDRESSES', values: nextAddresses });
    actor.send({
      type: 'ON_SUBMIT_CONTACT',
      values: { addresses: nextAddresses, emailAddresses, phoneNumbers },
    });
  };

  const addAddress = (address: AddressFormValues) => {
    persist([...addresses, address]);
  };

  const editAddress = (address: AddressFormValues) => {
    const updated = [...addresses];
    updated[editAddressIndex] = address;
    persist(updated);
  };

  const methods = useCustomForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: addressForm,
  });

  const { handleSubmit } = methods;
  const onSubmitError = (err: unknown) => console.debug('Address form validation error:', err);
  const onSubmitSuccess = (values: AddressFormValues) => {
    if (create) {
      addAddress(values);
    } else {
      editAddress(values);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-5 text-content-primary"
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="address-form"
      >
        <AddressForm />
        <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
      </form>
    </FormProvider>
  );
};
